import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Search, Sparkles, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, LoadingGrid, inr } from "@/components/bits";
import api, { errMsg } from "@/lib/api";

export default function Talent() {
  const [filters, setFilters] = useState({ skills: "", location: "", q: "" });
  const [candidates, setCandidates] = useState(null);
  const [usage, setUsage] = useState(null);
  const [gate, setGate] = useState(null);
  const [aiQuery, setAiQuery] = useState("");
  const [aiMatches, setAiMatches] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const search = async () => {
    setCandidates(null);
    setAiMatches(null);
    setGate(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      const { data } = await api.get(`/talent-search?${params.toString()}`);
      setCandidates(data.candidates);
      setUsage(data.usage);
    } catch (e) {
      if (e?.response?.status === 402) {
        setGate(errMsg(e));
        setCandidates([]);
      } else {
        toast.error(errMsg(e));
        setCandidates([]);
      }
    }
  };

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aiSearch = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/talent-search", { query: aiQuery });
      setAiMatches(data.matches);
      setCandidates([]);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAiBusy(false);
    }
  };

  const requestContact = async (candidateId) => {
    try {
      await api.post("/contact-requests", { candidate_id: candidateId, job_context: "an opportunity at your company" });
      toast.success("Contact request sent — candidate consent required");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const renderCard = (p, extra) => (
    <div key={p.user_id} className="bg-white border border-border/60 rounded-xl p-6 card-hover" data-testid={`talent-card-${p.user_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-heading font-semibold text-[#0B1B3D]">{p.name}</p>
          <p className="text-sm text-muted-foreground">{p.headline || "Professional"}</p>
        </div>
        {extra?.score != null && (
          <span className="font-heading font-extrabold text-xl text-[#2563EB]" data-testid={`talent-score-${p.user_id}`}>
            {extra.score}%
          </span>
        )}
      </div>
      <div className="flex gap-1.5 flex-wrap mt-3">
        {(p.skills || []).slice(0, 5).map((s) => (
          <span key={s} className="text-[11px] bg-[#2563EB]/8 text-[#2563EB] rounded-full px-2 py-0.5 font-medium">{s}</span>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        {p.location} · {p.experience_years} yrs · Exp. {inr(p.expected_ctc)} · Notice: {p.notice_period || "—"}
      </p>
      {extra?.explanation && (
        <p className="text-xs bg-[#F5A623]/10 border border-[#F5A623]/25 rounded-lg p-2.5 mt-3 text-slate-700">{extra.explanation}</p>
      )}
      <div className="mt-4">
        {p.contact_unlocked ? (
          <p className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 flex items-center gap-1.5">
            <Unlock className="w-3.5 h-3.5" /> {p.email} {p.phone && `· ${p.phone}`}
          </p>
        ) : (
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => requestContact(p.user_id)} data-testid={`request-contact-${p.user_id}`}>
            <Lock className="w-3.5 h-3.5 mr-1.5" /> Request Contact
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="talent-page">
      <div className="navy-bg rounded-xl p-6 mb-8">
        <p className="text-xs font-semibold tracking-widest uppercase text-[#F5A623] mb-3 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> AI Talent Search
        </p>
        <div className="flex gap-3">
          <Input
            value={aiQuery}
            onChange={(e) => setAiQuery(e.target.value)}
            placeholder='e.g. "Sales managers in Bengaluru with 3–7 years B2B experience, CRM skills, below ₹12 LPA, available in 30 days"'
            className="bg-white/10 border-white/15 text-white placeholder:text-white/40"
            data-testid="ai-talent-input"
          />
          <Button onClick={aiSearch} disabled={aiBusy || !aiQuery.trim()} className="bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-semibold rounded-full px-6 shrink-0" data-testid="ai-talent-btn">
            {aiBusy ? "Matching…" : "AI Match"}
          </Button>
        </div>
        <p className="text-[11px] text-white/40 mt-3">
          AI assists with ranked suggestions and explanations — final decisions always remain with your hiring team.
        </p>
      </div>

      {gate && (
        <div className="bg-[#F5A623]/12 border border-[#F5A623]/40 rounded-xl p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="talent-gate-banner">
          <p className="text-sm font-medium text-[#0B1B3D]">{gate}</p>
          <Button size="sm" className="rounded-full bg-[#0B1B3D]" onClick={() => (window.location.href = "/employer/billing")} data-testid="talent-upgrade-btn">
            View Plans
          </Button>
        </div>
      )}
      {usage && !usage.bundled && (
        <p className="text-xs text-muted-foreground mb-6" data-testid="talent-usage">
          Talent plan usage: {usage.views_used} / {usage.views_total} profile views used
        </p>
      )}

      <div className="grid sm:grid-cols-4 gap-3 mb-8">
        <Input placeholder="Skills (comma separated)" value={filters.skills} onChange={(e) => setFilters({ ...filters, skills: e.target.value })} data-testid="talent-skills-input" />
        <Input placeholder="Location" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} data-testid="talent-location-input" />
        <Input placeholder="Role or keyword" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="talent-q-input" />
        <Button onClick={search} className="rounded-full bg-[#0B1B3D]" data-testid="talent-search-btn">
          <Search className="w-4 h-4 mr-2" /> Search
        </Button>
      </div>

      {aiMatches && (
        <div className="mb-10">
          <h3 className="font-heading font-semibold text-lg text-[#0B1B3D] mb-4">AI-ranked matches</h3>
          {aiMatches.length === 0 ? (
            <EmptyState title="No strong matches found" description="Try a broader query." testId="ai-talent-empty" />
          ) : (
            <div className="grid md:grid-cols-2 gap-5">{aiMatches.map((m) => renderCard(m.profile, m))}</div>
          )}
        </div>
      )}

      {candidates === null && !aiMatches ? (
        <LoadingGrid count={4} />
      ) : candidates.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-5" data-testid="talent-results">{candidates.map((p) => renderCard(p))}</div>
      ) : (
        !aiMatches && <EmptyState title="No candidates found" description="Adjust filters or try AI search." testId="talent-empty" />
      )}
    </div>
  );
}
